/**
 * Утилиты для страницы детального просмотра посадки
 */
import dayjs from 'dayjs';
import i18n, { formatDate } from '../../../i18n';
import { kindName, QUANTITY_AFFECTING_KINDS } from './constants';
import type { PlantingEvent } from '../../../types/plantingEvents';

/**
 * Нужно ли показывать quantityAffected:
 * - только для частично выполненных (qaff < total)
 * - или для событий, влияющих на кол-во
 */
export const shouldShowQuantityAffected = (
  event: PlantingEvent,
  totalPlantingQty?: number | null,
): boolean => {
  if (!event.quantityAffected) return false;
  const totalQty = totalPlantingQty ?? event.plantingQuantity ?? 1;
  if (event.quantityAffected < totalQty) return true;
  const code = event.templateCode ?? event.type ?? '';
  if (QUANTITY_AFFECTING_KINDS.has(code)) return true;
  return false;
};

export const buildAppliedHarvestBaseById = (
  events: PlantingEvent[],
  totalPlantingQty?: number | null,
  initialPlantingQty?: number | null,
): Map<string, number> => {
  const appliedHarvests = events
    .filter((event) => (
      isAppliedQuantityAffectedEvent(event)
      && shouldShowQuantityAffected(event, totalPlantingQty)
      && (event.quantityAffected ?? 0) > 0
    ))
    .map((event) => ({
      id: event.id,
      qty: event.quantityAffected ?? 0,
      date: resolveEventDate(event) ?? '',
    }))
    .sort((left, right) => left.date.localeCompare(right.date));

  const totalHarvested = appliedHarvests.reduce((sum, harvest) => sum + harvest.qty, 0);
  const initialStand = initialPlantingQty ?? ((totalPlantingQty ?? 0) + totalHarvested);
  const map = new Map<string, number>();
  let consumed = 0;

  for (const harvest of appliedHarvests) {
    map.set(harvest.id, initialStand - consumed);
    consumed += harvest.qty;
  }

  return map;
};

/**
 * Формирует человекочитаемое имя события.
 * Приоритет: templateName > kind name > kind generic label > type
 */
export const getEventDisplayName = (event: PlantingEvent): string => {
  if (event.templateName) return event.templateName;
  const fromKind = kindName(event.kind);
  if (fromKind) return fromKind;
  if (event.kind === 'measurement') return i18n.t('plantings:eventName.measurement');
  if (event.kind === 'service') return i18n.t('plantings:eventName.service');

  switch (event.type) {
    case 'created': return i18n.t('plantings:eventKind.created');
    case 'status_changed': return i18n.t('plantings:eventKind.status_changed');
    case 'deleted': return i18n.t('plantings:eventName.deleted');
    case 'measurement': return i18n.t('plantings:eventName.measurement');
    case 'planting': return i18n.t('plantings:eventName.planting');
    default: return event.type || i18n.t('plantings:eventName.generic');
  }
};

export const getEventSemanticKind = (event: PlantingEvent): string => {
  const propertyKind = event.properties?.find((prop) => prop.propertyCode === 'planting_event_kind')?.valueText;
  return String(propertyKind || event.kind || event.templateCode || event.type || '').toLowerCase();
};

export const isAppliedQuantityAffectedEvent = (event: PlantingEvent): boolean => {
  if (event.status !== 'completed') return false;
  const decisionState = String(event.decisionState || 'confirmed').toLowerCase();
  return decisionState === 'confirmed';
};

export const isImmutableAppliedEvent = (event: PlantingEvent): boolean => {
  const semanticKind = getEventSemanticKind(event);
  if (semanticKind === 'created') return true;
  return isAppliedQuantityAffectedEvent(event)
    && QUANTITY_AFFECTING_KINDS.has(semanticKind);
};

/**
 * Возвращает календарный день без timezone-конвертации.
 */
export const toDateOnly = (value?: string | null): string | null => {
  if (!value) return null;
  const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : null;
};

export const toDayjsDateOnly = (value?: string | null): dayjs.Dayjs | null => {
  const date = toDateOnly(value);
  if (!date) return null;
  const parsed = dayjs(date);
  return parsed.isValid() ? parsed : null;
};

/**
 * Форматирует дату события как календарный день. Event time intentionally
 * hidden: events are day-scoped in simulation.
 */
export const formatEventDateTime = (value?: string | null): string => {
  const date = toDateOnly(value);
  return date && dayjs(date).isValid() ? formatDate(date, 'short') : String(value || '—');
};

export const resolveEventDate = (event: PlantingEvent): string | null => {
  return event.actualDate || event.plannedDate || null;
};

export const resolveEditableEventDateField = (
  event: PlantingEvent,
): 'actualDate' | 'plannedDate' => {
  return event.status === 'completed' ? 'actualDate' : 'plannedDate';
};

export const resolveEditableEventDateLabel = (event?: PlantingEvent | null): string => {
  if (!event) return i18n.t('plantings:eventDate.event');
  return event.status === 'completed' ? i18n.t('plantings:eventDate.actual') : i18n.t('plantings:eventDate.planned');
};

/**
 * Формирует текстовое описание перемещения посадки из JSON-payload локации.
 */
export const renderLocationPayloadSummary = (
  raw?: string | null,
  locations: any[] = [],
): string | null => {
  if (!raw) return null;
  try {
    const locationByBedId = new Map<string, any>();
    locations.forEach((location) => {
      const bedId = String(location?.bed_id || location?.bed?.id || '');
      if (bedId) locationByBedId.set(bedId, location);
    });

    const toBedLabel = (item: any): string => {
      const bedId = String(item?.bedId || item?.bed_id || '');
      const location = bedId ? locationByBedId.get(bedId) : null;
      const fieldName = location?.field?.name ? i18n.t('plantings:location.fieldNamed', { name: location.field.name }) : null;
      const plotName = location?.plot?.name ? i18n.t('plantings:location.plotNamed', { name: location.plot.name }) : null;
      const bedName = location?.bed?.name || location?.bed?.anchor_code
        || (bedId ? i18n.t('plantings:location.bedNamed', { name: bedId.slice(0, 8) }) : i18n.t('plantings:location.bed'));
      return [fieldName, plotName, bedName].filter(Boolean).join(' / ');
    };

    const toDateLabel = (value?: string | null): string => {
      if (!value) return '';
      const parsed = dayjs(value);
      return parsed.isValid() ? formatDate(toDateOnly(value) ?? value, 'short') : String(value);
    };

    const formatEntries = (
      entries: any[],
      actionLabel: string,
      dateKey: 'fromDate' | 'closedAt' | 'toDate',
    ): string[] => {
      if (!Array.isArray(entries) || entries.length === 0) return [];
      return entries.map((entry) => {
        const bed = toBedLabel(entry);
        const date = toDateLabel(entry?.[dateKey]);
        return date
          ? i18n.t('plantings:locationChange.entryWithDate', { action: actionLabel, bed, date })
          : i18n.t('plantings:locationChange.entry', { action: actionLabel, bed });
      });
    };

    const payload = JSON.parse(raw);
    const parts: string[] = [
      ...formatEntries(payload?.added || [], i18n.t('plantings:locationChange.added'), 'fromDate'),
      ...formatEntries(payload?.updated || [], i18n.t('plantings:locationChange.updated'), 'fromDate'),
      ...formatEntries(payload?.closed || [], i18n.t('plantings:locationChange.closed'), 'closedAt'),
    ];
    return parts.length > 0 ? parts.join('; ') : i18n.t('plantings:locationChange.updatedGeneric');
  } catch {
    return i18n.t('plantings:locationChange.updatedGeneric');
  }
};

/** Нормализация числа для осей графика (борьба с floating-point артефактами) */
export const normalizeChartNumber = (value: unknown, precision = 4): number => {
  const num = Number(value);
  if (!Number.isFinite(num)) return 0;
  const factor = 10 ** precision;
  return Math.round((num + Number.EPSILON) * factor) / factor;
};

/** Доступ к вложенному свойству по точечному пути (e.g. 'dimensions.height') */
export const getMetricValue = (point: any, key: string): unknown => {
  if (!point || !key) return undefined;
  return key.split('.').reduce((acc, part) => (acc == null ? undefined : acc[part]), point);
};

/** Форматирование числа для оси графика */
export const formatAxisNumber = (value: unknown, precision = 2): string => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '0';
  const normalized = normalizeChartNumber(num, precision + 2);
  if (Math.abs(normalized) < 1e-9) return '0';
  return String(Number(normalized.toFixed(precision)));
};
